/**
 * Brand — two levels, every product-name string in one place.
 * @file src/web/shared/brand.ts
 *
 * @llm-rule WHEN: Rendering a product name or tagline anywhere in the UI
 * @llm-rule AVOID: Hardcoding "Voilacode" or "Presynce" in a component — import BRAND
 * @llm-rule NOTE: Two brands, deliberately. PLATFORM is what the super admin
 *   signs into to manage customer companies. PRODUCT is what people inside a
 *   company actually use every day. Renaming either is a one-line change here.
 */

/** The platform. What the super admin manages companies from. */
export const PLATFORM = {
  name: 'Voilacode',
  tagline: 'Every company, one platform.',
  subtitle:
    'Add a company, hand its CEO the keys, and stay out of their data. Voilacode runs the platform — what happens inside each company is theirs.',
} as const;

/** The product inside each company. What employees see all day. */
export const PRODUCT = {
  name: 'Presynce',
  tagline: 'Your face is your badge.',
  subtitle:
    'Face-verified attendance, tied to the place it happened — and a straight line between the people who lead and the people who deliver.',

  /** Alternates kept for the naming decision. Nothing reads these directly. */
  taglineOptions: [
    'Your face is your badge.',
    'Presence, proven.',
    'Show up. That’s the whole login.',
    'Attendance that knows where it happened.',
  ],
} as const;

/**
 * Back-compat alias. Components inside the company app that just want "the
 * name" get the product; the platform console imports PLATFORM explicitly.
 */
export const BRAND = PRODUCT;

/** Browser tab title: "Presynce · Acme Corp" once a company is known. */
export function pageTitle(organisationName?: string | null): string {
  if (!organisationName) return `${PRODUCT.name} — ${PRODUCT.tagline}`;
  return `${PRODUCT.name} · ${organisationName}`;
}
